import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, Typography, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { copyText } from "../../utils/copyText";
import { registerEdge, issueDeviceClaim } from "../../api/edgeApi";
import { NO_LOCATION } from "./treeHelpers";
import { formatDate } from "../../utils/i18nFormat";

// Per-edge claim. Same flow as EdgeList: registerEdge (now bound to a location) -> issueDeviceClaim (one-time code).
export default function ClaimDialog({ open, tenantId, locationId, onClose, onCreated }) {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  // Tier and cloud transfer are what the customer paid for: only VALONIX (superadmin) assigns them. A tenant admin
  // registers a Lite edge without cloud transfer; the backend enforces the same, this just hides the choice.
  const isSuper = useSelector((s) => s.session?.me?.global_role === "superadmin");
  const [name, setName] = useState("");
  // Kasıtlı olarak varsayılansız (boş) bırakıldı: ikisi de sonradan değişince
  // cihazın yeniden enrollment olması gerekiyor, o yüzden ilk seçim bilinçli
  // olmalı, sessizce "pro" / "kapalı" seçilmemeli.
  const [tier, setTier] = useState("");
  const [cloudTransfer, setCloudTransfer] = useState("");
  const [model, setModel] = useState("");
  const [firmware, setFirmware] = useState("");
  const [busy, setBusy] = useState(false);
  const [claim, setClaim] = useState(null);
  const claimRequest = useRef(0);

  const close = () => {
    setName("");
    setTier("");
    setCloudTransfer("");
    setModel("");
    setFirmware("");
    setClaim(null);
    onClose();
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error(t("edges.claim.nameRequired"));
      return;
    }
    if (isSuper && !tier) {
      toast.error(t("edges.claim.tierRequired"));
      return;
    }
    if (isSuper && !cloudTransfer) {
      toast.error(t("edges.claim.cloudRequired"));
      return;
    }
    if (!model.trim()) {
      toast.error(t("edges.claim.modelRequired"));
      return;
    }
    setBusy(true);
    try {
      const edge = await registerEdge(accessToken, tenantId, {
        name: name.trim(),
        location_id: locationId && locationId !== NO_LOCATION ? locationId : null,
        tier: isSuper ? tier : "lite",
        cloud_transfer_enabled: isSuper && cloudTransfer === "yes",
        device: { manufacturer: "", model: model.trim(), firmware: firmware.trim() },
        asset: { assetId: "", type: "", location: "" },
      });
      onCreated();
      const requestId = ++claimRequest.current;
      try {
        const issued = await issueDeviceClaim(accessToken, edge.id);
        if (requestId !== claimRequest.current) return;
        if (issued?.edge_uuid !== edge.id) {
          toast.error(t("edges.claim.mismatch"));
          close();
          return;
        }
        setClaim(issued);
      } catch (err) {
        toast.warning(err.response?.data?.detail || t("edges.claim.issueFailed"));
        close();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("edges.claim.createFailed"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (await copyText(claim.claim_code)) toast.success(t("edges.claim.copied"));
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border)" } }}>
      <DialogTitle sx={{ borderBottom: "1px solid var(--border)" }}>{claim ? t("edges.claim.titleInfo") : t("edges.claim.titleCreate")}</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {claim ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("edges.claim.warning")}
            </Alert>
            <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>{t("edges.claim.edgeName")}</Typography>
            <Typography sx={{ fontFamily: "monospace", mb: 2, wordBreak: "break-all" }}>{claim.edge_id}</Typography>
            <Typography variant="body2" sx={{ color: "var(--text-muted)", mb: 1 }}>{t("edges.claim.oneTimeCode")}</Typography>
            <div style={{ padding: 16, background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 6, fontFamily: "monospace", wordBreak: "break-all" }}>
              {claim.claim_code}
            </div>
            <Typography variant="caption" sx={{ color: "var(--text-muted)", mt: 1, mb: 2, display: "block" }}>
              {t("edges.claim.expires", { date: formatDate(claim.expires_at) })}
            </Typography>
            <ol style={{ margin: 0, paddingLeft: 20, color: "var(--text-muted)", fontSize: 14, lineHeight: 1.7 }}>
              <li>{t("edges.claim.step1")}</li>
              <li>{t("edges.claim.step2")}</li>
              <li>{t("edges.claim.step3")}</li>
            </ol>
          </>
        ) : (
          <>
            <TextField
              autoFocus
              fullWidth
              label={t("edges.claim.nameLabel")}
              helperText={t("edges.claim.nameHelp")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && submit()}
              sx={{ mt: 1, "& .MuiInputBase-root": { color: "var(--text-primary)" }, "& .MuiInputLabel-root": { color: "var(--text-muted)" } }}
            />
            {isSuper && (<>
            <FormControl sx={{ mt: 3 }} required>
              <FormLabel sx={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}>{t("edges.claim.tierLabel")}</FormLabel>
              <RadioGroup row value={tier} onChange={(e) => setTier(e.target.value)}>
                <FormControlLabel value="lite" control={<Radio />} label={t("edges.claim.tierLite")} />
                <FormControlLabel value="pro" control={<Radio />} label={t("edges.claim.tierPro")} />
              </RadioGroup>
              <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
                {t("edges.claim.changeNote")}
              </Typography>
            </FormControl>
            <FormControl sx={{ mt: 2 }} required>
              <FormLabel sx={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 14 }}>{t("edges.claim.cloudLabel")}</FormLabel>
              <RadioGroup row value={cloudTransfer} onChange={(e) => setCloudTransfer(e.target.value)}>
                <FormControlLabel value="yes" control={<Radio />} label={t("edges.claim.on")} />
                <FormControlLabel value="no" control={<Radio />} label={t("edges.claim.off")} />
              </RadioGroup>
              <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
                {t("edges.claim.changeNote")}
              </Typography>
            </FormControl>
            </>)}
            <TextField
              fullWidth
              label={t("edges.claim.modelLabel")}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{ mt: 3, "& .MuiInputBase-root": { color: "var(--text-primary)" }, "& .MuiInputLabel-root": { color: "var(--text-muted)" } }}
            />
            <TextField
              fullWidth
              label={t("edges.claim.firmwareLabel")}
              helperText={t("edges.claim.firmwareHelp")}
              value={firmware}
              onChange={(e) => setFirmware(e.target.value)}
              sx={{ mt: 2, "& .MuiInputBase-root": { color: "var(--text-primary)" }, "& .MuiInputLabel-root": { color: "var(--text-muted)" } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid var(--border)", p: 2 }}>
        {claim ? (
          <>
            <Button onClick={copy} sx={{ color: "var(--accent-navy-light)" }}>{t("edges.claim.copy")}</Button>
            <Button onClick={close} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("edges.claim.closeDelete")}</Button>
          </>
        ) : (
          <>
            <Button onClick={close} sx={{ color: "var(--text-muted)" }}>{t("edges.cancel")}</Button>
            <Button onClick={submit} disabled={busy} variant="contained" sx={{ backgroundColor: "var(--btn-primary)" }}>{t("edges.claim.generate")}</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
